import type { JSX } from "react";
import { clockText, compact, full, LABEL, money, remain, ringWindow, win, type DashboardData, type Provider } from "../data.js";
import { Fit, Logo, PageHead, Pill, QuotaRow, Ring, textWidth, tileBasis } from "../ui.js";

type Tone = "red" | "blue" | "white" | "cyan";
const TONES: Record<Provider, [Tone, Tone, Tone, Tone]> = { claude: ["red", "red", "blue", "red"], codex: ["white", "cyan", "cyan", "cyan"] };

export function StatBox({ label, text, fallback, tone }: { label: string; text: string; fallback?: string; tone: Tone }): JSX.Element {
  return (
    <div className="cx-box" style={{ flex: tileBasis(label, text, 10.4, 15, 14), minWidth: Math.ceil(textWidth(label, 10.4)) + 14 }}>
      <small>{label}</small>
      <Fit key={text} className={`v ${tone}`} max={15} min={10} {...(fallback !== undefined ? { fallback } : {})}>{text}</Fit>
    </div>
  );
}

export function Home({ dashboard, error, clock, onRefresh, onDetail }: { dashboard: DashboardData | null; error: string; clock: number; onRefresh: () => void; onDetail: (provider: Provider) => void }): JSX.Element {
  const providers = dashboard?.providers ?? [];
  return (
    <>
      <PageHead title="AI 用量看板" subtitle="实时 · 简洁 · 高效" right={<>
        <span className="cx-chip num">{clockText(clock)}</span>
        <button className="cx-btn red solid" onClick={onRefresh}>刷新</button>
      </>} />
      {error && <p className="cx-error">{error}</p>}
      <div className="cx-grid2 cx-fill">
        {providers.map((item) => {
          const ring = ringWindow(item.windows);
          const week = win(item.windows, 10080);
          const usage = item.tokenUsage;
          const tones = TONES[item.provider];
          return (
            <section className={`cx-card neon ${item.provider} cx-home-card`} key={item.provider} role="button" tabIndex={0} onClick={() => onDetail(item.provider)} onKeyDown={(event) => { if (event.key === "Enter") onDetail(item.provider); }}>
              <header className="cx-card-head">
                <Logo provider={item.provider} size={24} />
                <h2>{LABEL[item.provider]}</h2>
                <Pill tone={item.stale ? "warn" : "ok"}>{item.stale ? "数据过期" : "已连接"}</Pill>
              </header>
              <div className="cx-home-mid">
                <Ring value={ring.value} label={ring.label} provider={item.provider} size={116} stroke={11} />
                <div className="cx-quota">
                  <QuotaRow label="5 小时用量" value={win(item.windows, 300)?.usedPercent ?? null} provider={item.provider} />
                  <QuotaRow label="7 天用量" value={week?.usedPercent ?? null} provider={item.provider} />
                  <p className="cx-left">{week?.resetsAt ? `还剩 ${remain(week.resetsAt)}` : "重置时间未知"}</p>
                </div>
              </div>
              <div className="cx-boxes">
                <StatBox label="今日 Token" text={full(usage.todayTokens)} fallback={compact(usage.todayTokens)} tone={tones[0]} />
                <StatBox label="累计 Token" text={compact(usage.totalTokens)} tone={tones[1]} />
                <StatBox label="今日费用" text={money(usage.todayCostUsd)} tone={tones[2]} />
                <StatBox label="累计费用" text={money(usage.totalCostUsd)} tone={tones[3]} />
              </div>
            </section>
          );
        })}
        {!providers.length && <section className="cx-card neutral"><div className="cx-empty">还没有采集器上报数据。</div></section>}
      </div>
      <p className="cx-footnote">费用为 API 等价估算，非账单金额</p>
    </>
  );
}
